<template>
  <div class="login-container">
    <div class="login-card">
      <h2>工单管理系统</h2>

      <!-- 第一步：用户名 + 密码 -->
      <form v-if="step === 1" @submit.prevent="handleLogin">
        <div class="form-group">
          <label>用户名</label>
          <el-input v-model="username" placeholder="请输入用户名"/>
        </div>
        <div class="form-group">
          <label>密码</label>
          <el-input
              v-model="password"
              type="password"
              placeholder="请输入密码"
              show-password
          />
        </div>
        <div class="form-links">
          <router-link to="/auth/forgot-password" class="forgot-link">忘记密码？</router-link>
        </div>
        <el-button
            type="primary"
            @click="handleLogin"
            :loading="loading"
            class="login-button"
        >
          登录
        </el-button>
      </form>

      <!-- 第二步：两步验证 -->
      <form v-else @submit.prevent="handleVerify">
        <div class="mfa-tip">
          <el-icon><Lock/></el-icon>
          <span>账号已开启两步验证，请完成二次验证</span>
        </div>

        <!-- 验证方式切换（仅当多种方式可用时显示） -->
        <div class="form-group" v-if="mfaInfo.methods && mfaInfo.methods.length > 1">
          <label>验证方式</label>
          <el-radio-group v-model="mfaMethod" @change="onMethodChange">
            <el-radio-button v-if="mfaInfo.methods.includes('email')" label="email">
              邮箱验证码
            </el-radio-button>
            <el-radio-button v-if="mfaInfo.methods.includes('authenticator')" label="authenticator">
              验证器动态码
            </el-radio-button>
          </el-radio-group>
        </div>

        <!-- 邮箱验证码方式 -->
        <template v-if="mfaMethod === 'email'">
          <div class="mfa-email-tip" v-if="mfaInfo.email_masked">
            验证码将发送至：<strong>{{ mfaInfo.email_masked }}</strong>
          </div>
          <div class="form-group">
            <label>邮箱验证码</label>
            <div class="code-row">
              <el-input
                  v-model="mfaCode"
                  placeholder="请输入6位验证码"
                  maxlength="10"
                  @keyup.enter="handleVerify"
              />
              <el-button
                  :disabled="countdown > 0"
                  :loading="sendingCode"
                  @click="sendCode"
              >
                {{ countdown > 0 ? `${countdown}秒后重发` : '获取验证码' }}
              </el-button>
            </div>
          </div>
        </template>

        <!-- 验证器动态码方式 -->
        <template v-else-if="mfaMethod === 'authenticator'">
          <div class="form-group">
            <label>Aegis Authenticator 动态码</label>
            <el-input
                v-model="mfaCode"
                placeholder="请输入验证器6位动态码"
                maxlength="10"
                @keyup.enter="handleVerify"
            />
            <div class="mfa-tip-small">请打开手机验证器 App 查看当前动态码（每 30 秒刷新）</div>
          </div>
        </template>

        <el-button
            type="primary"
            @click="handleVerify"
            :loading="verifying"
            class="login-button"
        >
          验证并登录
        </el-button>
        <el-button text class="back-button" @click="backToStep1" :disabled="verifying">
          返回上一步
        </el-button>
      </form>
    </div>
  </div>
</template>

<script setup>
import {onBeforeUnmount, ref} from 'vue'
import {useRouter} from 'vue-router'
import {ElMessage} from 'element-plus'
import Cookies from 'js-cookie'
import {Lock} from '@element-plus/icons-vue'
import {login, sendTwoFactorEmail, verifyTwoFactor} from '@/api/auth'
import {useAuthStore} from "@/store/auth";
import {useAuthStore as useMainAuthStore} from "@/stores/auth";

const router = useRouter()
const authStore = useAuthStore()
// 主 store：路由守卫（guards.js）与各页面布局均使用 @/stores/auth，
// 登录成功后需同步更新，保证跳转与用户信息展示正常
const mainAuthStore = useMainAuthStore()

// 步骤：1=账号密码，2=两步验证
const step = ref(1)
const username = ref('')
const password = ref('')
const loading = ref(false)
const verifying = ref(false)
const sendingCode = ref(false)
const countdown = ref(0)
let countdownTimer = null

// 两步验证信息（登录接口返回）
const mfaInfo = ref({
  mfa_ticket: '',
  methods: [],
  method: 'email',
  email_masked: ''
})
const mfaMethod = ref('email')
const mfaCode = ref('')

// 提取错误信息（兼容后端自定义异常 message 与标准 HTTPException detail）
const extractError = (error, fallback) => {
  return error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.error ||
      error.message ||
      fallback
}

// 保存令牌并跳转
const finishLogin = async (data) => {
  const {access_token, refresh_token, user} = data || {}

  if (!access_token || !refresh_token || !user) {
    ElMessage.error('登录响应数据不完整')
    return
  }

  if (access_token) {
    Cookies.set('access_token', access_token, {expires: 7})
  }
  if (refresh_token) {
    Cookies.set('refresh_token', refresh_token, {expires: 30})
  }

  authStore.setUser(user)
  authStore.setTokens(access_token, refresh_token)

  // 同步主 store（路由守卫 isAuthenticated / 布局用户信息 / WebSocket 均依赖）
  mainAuthStore.setTokens(access_token, refresh_token)
  mainAuthStore.setUser(user)

  ElMessage.success('登录成功')

  await new Promise(resolve => setTimeout(resolve, 100))

  window.dispatchEvent(new CustomEvent('user-login-success', {
    detail: {
      user: user,
      access_token: access_token,
      timestamp: new Date().getTime()
    }
  }))

  await router.push('/dashboard')
}

const handleLogin = async () => {
  if (!username.value.trim()) {
    ElMessage.warning('请输入用户名')
    return
  }

  if (!password.value.trim()) {
    ElMessage.warning('请输入密码')
    return
  }

  try {
    loading.value = true

    const response = await login(username.value, password.value)

    // 后端登录接口两种返回：
    // 1. 直接登录成功：{ access_token, refresh_token, user, ... }
    // 2. 需要两步验证：{ code, message, data: { mfa_required, mfa_ticket, ... } }
    const mfaData = response?.data?.mfa_required ? response.data : null

    if (mfaData) {
      // 进入两步验证步骤
      mfaInfo.value = {
        mfa_ticket: mfaData.mfa_ticket || '',
        methods: mfaData.methods || [],
        method: mfaData.method || 'email',
        email_masked: mfaData.email_masked || ''
      }
      mfaMethod.value = mfaInfo.value.method === 'authenticator' ? 'authenticator' : 'email'
      mfaCode.value = ''
      step.value = 2

      // 邮箱验证方式：进入后主动触发一次验证码发送（无需用户手动点击）
      if (mfaMethod.value === 'email') {
        sendCode()
      } else {
        ElMessage.info('请输入验证器动态码完成二次验证')
      }
      return
    }

    // 直接登录成功
    const loginData = response?.data || response
    await finishLogin(loginData)

  } catch (error) {
    console.error('登录失败:', error)
    ElMessage.error(extractError(error, '登录失败，请检查用户名和密码'))
  } finally {
    loading.value = false
  }
}

// 发送邮箱验证码（登录流程携带 mfa_ticket）
const sendCode = async () => {
  try {
    sendingCode.value = true
    const response = await sendTwoFactorEmail(mfaInfo.value.mfa_ticket)

    const data = response?.data || {}
    ElMessage.success(data.message || '验证码已发送，请查收邮箱')

    // 启动重发倒计时（后端默认 60 秒）
    const resendAfter = data.resend_after || 60
    countdown.value = resendAfter
    clearInterval(countdownTimer)
    countdownTimer = setInterval(() => {
      countdown.value -= 1
      if (countdown.value <= 0) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }, 1000)
  } catch (error) {
    console.error('发送验证码失败:', error)
    ElMessage.error(extractError(error, '验证码发送失败'))
  } finally {
    sendingCode.value = false
  }
}

// 切换验证方式时清空已输入的验证码；切到邮箱方式时主动发送验证码
const onMethodChange = () => {
  mfaCode.value = ''
  if (mfaMethod.value === 'email') {
    sendCode()
  }
}

const handleVerify = async () => {
  if (!mfaCode.value.trim()) {
    ElMessage.warning(mfaMethod.value === 'email' ? '请输入邮箱验证码' : '请输入验证器动态码')
    return
  }

  try {
    verifying.value = true

    const response = await verifyTwoFactor({
      mfa_ticket: mfaInfo.value.mfa_ticket,
      method: mfaMethod.value,
      code: mfaCode.value.trim()
    })

    // 校验通过后返回与登录一致的令牌数据
    const loginData = response?.data || response
    await finishLogin(loginData)

  } catch (error) {
    console.error('两步验证失败:', error)
    ElMessage.error(extractError(error, '验证失败，请检查验证码'))
  } finally {
    verifying.value = false
  }
}

const backToStep1 = () => {
  step.value = 1
  mfaCode.value = ''
  mfaInfo.value = {
    mfa_ticket: '',
    methods: [],
    method: 'email',
    email_masked: ''
  }
  clearInterval(countdownTimer)
  countdownTimer = null
  countdown.value = 0
}

onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--gradient-primary);
  position: relative;
  overflow: hidden;
}

.login-container::before {
  content: '';
  position: absolute;
  width: 200%;
  height: 200%;
  background: radial-gradient(ellipse at center, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: float 6s ease-in-out infinite;
}

.login-card {
  background: var(--bg-overlay);
  padding: 40px;
  border-radius: 16px;
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 400px;
  backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  position: relative;
  z-index: 1;
  animation: scaleIn 0.4s ease;
}

.login-card h2 {
  text-align: center;
  margin-bottom: 30px;
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-regular);
  font-weight: 500;
}

.form-links {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.forgot-link {
  color: var(--app-primary);
  font-size: 13px;
  text-decoration: none;
}

.forgot-link:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  margin-top: 10px;
  height: 40px;
  font-size: 16px;
}

.back-button {
  width: 100%;
  /* 覆盖 Element Plus 相邻按钮的 margin-left: 12px，保证与"验证并登录"按钮左对齐 */
  margin: 10px 0 0 0 !important;
}

.mfa-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(64, 158, 255, 0.08);
  border: 1px solid rgba(64, 158, 255, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 20px;
  color: var(--app-primary);
  font-size: 14px;
}

.mfa-email-tip {
  margin-bottom: 12px;
  color: var(--text-secondary);
  font-size: 13px;
}

.code-row {
  display: flex;
  gap: 10px;
}

.code-row .el-input {
  flex: 1;
}

.code-row .el-button {
  white-space: nowrap;
}

.mfa-tip-small {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
}

@keyframes scaleIn {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
